import { Injectable } from '@angular/core';
import { Settings } from '../../../core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, startWith } from 'rxjs/operators';
import { deserialize } from 'santee-dcts';
import { EQueryAdminSummariesReadModel, EQueryAdminSummary } from '../models/e-query-admin-summary';
import { prepareFilterUrlParams } from '../../../documents/prepare-filter-url-params';
import { EQueryAdminSummaryFilterItem, EQueryAdminSummaryFilterItemsReadModel } from '../models/e-query-admin-summary-filter-item';
import { FilterFieldValue } from '../../../covance-filter';
import { FilterUrlParamsPreparer } from '../e-query-summary/filter-url-params-preparer';
import { Protocol } from '../../shared';

interface SearchValues {
    readonly protocol: object;
    readonly accessionNumber: object;
}

@Injectable()
export class EQueryHideStorage {
    private readonly refresher = new BehaviorSubject<boolean>(true);
    constructor(
        protected readonly settings: Settings,
        protected readonly http: HttpClient,
        private readonly filterUrlParamsPreparer: FilterUrlParamsPreparer
    ) {
    }

    public forceLoadEQueries() {
        this.refresher.next(true);
    }

    public getEQuerySummaries(searchValues: SearchValues, filterFields: FilterFieldValue[]): Observable<EQueryAdminSummary[]> {
        if (this.areSearchFieldsEmpty(searchValues)) {
            return of([]);
        }

        const filtersParams = this.filterUrlParamsPreparer.prepareFilterUrlParams(filterFields);
        let params = this.prepareSearchFieldsParams(searchValues, filtersParams);
        
        return this.refresher.pipe(
            switchMap(() => this.http.get(`${this.settings.apiUrl}/admin/e-queries`, {params: params})
                .pipe(
                    map((response) => deserialize(response, EQueryAdminSummariesReadModel).eQueries)
                )
            ),
            startWith(null),
        );
    }

    public getEQueryFilterItems(fields$: Observable<FilterFieldValue[]>): Observable<EQueryAdminSummaryFilterItem[]> {
        return fields$.pipe(switchMap((fields) => {
            const params = prepareFilterUrlParams(fields);
            return this.http.get(
                `${this.settings.apiUrl}/admin/e-query-filter-items`, {params: params}
            ).pipe(
                map((r) => deserialize(r, EQueryAdminSummaryFilterItemsReadModel).filterItems)
            );
        }));
    }

    public hideEQuery(eQueryId: number, request: HideEQueryRequest): Promise<Object> {
        return this.http
            .post(`${this.settings.apiUrl}/admin/e-queries/${eQueryId}/hide`, request)
            .toPromise();
    }

    private areSearchFieldsEmpty(searchValues: SearchValues) {
        return !searchValues.protocol
            && !searchValues.accessionNumber;
    }

    private prepareSearchFieldsParams(searchValues: SearchValues, httpParams: HttpParams): HttpParams {

        if (searchValues.protocol) {
            httpParams = httpParams.append('protocol', (searchValues.protocol as Protocol).id.toString());
        }
        if (searchValues.accessionNumber) {
            httpParams = httpParams.append('accessionNumber', searchValues.accessionNumber.toString());
        }
        return httpParams;
    }
}

export interface HideEQueryRequest {
    reason: string;
}
